import React, { useEffect, useState } from "react";
import { PageHeader } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { api, apiErr } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { Plus, Wifi, Loader2, Copy, Trash2, ServerCog, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Routers() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { effectiveStructure, isSuperAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [structures, setStructures] = useState([]);
  const [open, setOpen] = useState(false);
  const [wizard, setWizard] = useState(null); // {router, config}
  const [testing, setTesting] = useState(null);
  const [form, setForm] = useState({ structure_id: "", name: "", model: "", ros_version: "", host: "", api_port: 8728, username: "admin", password: "", live_mode: false, notes: "" });

  const load = () => {
    const p = effectiveStructure ? `?structure_id=${effectiveStructure}` : "";
    api.get(`/routers${p}`).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); api.get("/structures").then((r) => setStructures(r.data)); }, [effectiveStructure]);

  const create = async () => {
    try {
      await api.post("/routers", { ...form, api_port: Number(form.api_port) });
      toast.success(t("common.save") + " ✓"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const test = async (r) => {
    setTesting(r.id);
    try {
      const { data } = await api.post(`/routers/${r.id}/test`);
      data.success ? toast.success(`${data.message} · ${data.latency_ms}ms`) : toast.error(data.message);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setTesting(null); }
  };

  const openWizard = async (r) => {
    const { data } = await api.get(`/routers/${r.id}/config`);
    setWizard({ router: r, config: data });
  };

  const del = async (r) => { await api.delete(`/routers/${r.id}`); toast.success("✓"); load(); };

  const toggleLive = async (r) => {
    try {
      const { data } = await api.post(`/routers/${r.id}/live-mode`, { enabled: !r.live_mode });
      toast.success(data.live_mode ? "Modalità Live attivata (MikroTik reale)" : "Tornato in modalità simulata");
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="routers-page">
      <PageHeader title={t("routers.title")} action={
        <button data-testid="new-router-btn" onClick={() => { setForm({ structure_id: effectiveStructure || structures[0]?.id || "", name: "", model: "", ros_version: "", host: "", api_port: 8728, username: "admin", password: "", live_mode: false, notes: "" }); setOpen(true); }}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2.5 text-sm font-semibold transition-colors">
          <Plus className="h-4 w-4" /> {t("routers.new")}
        </button>} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((r) => (
          <div key={r.id} data-testid={`router-card-${r.id}`} onClick={() => navigate(`/routers/${r.id}`)} className="rounded-xl border border-[#1F2937] bg-[#111827] p-5 cursor-pointer hover:border-cyan-600/40 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-cyan-500/10 ring-1 ring-cyan-500/20 flex items-center justify-center"><Wifi className="h-5 w-5 text-cyan-400" /></div>
                <div>
                  <h3 className="font-display font-semibold text-sm">{r.name}</h3>
                  <p className="text-xs text-slate-500 font-mono">{r.host}:{r.api_port}</p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <StatusBadge status={r.status} label={r.status === "online" ? t("routers.online") : t("routers.offline")} />
                {r.live_mode
                  ? <span data-testid={`router-live-${r.id}`} className="text-[9px] uppercase font-semibold tracking-wider bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30 px-1.5 py-0.5 rounded-full">● LIVE</span>
                  : <span className="text-[9px] uppercase font-semibold tracking-wider bg-slate-500/15 text-slate-400 px-1.5 py-0.5 rounded-full">SIMULATO</span>}
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-500 space-y-0.5">
              <div>{r.model} · RouterOS {r.ros_version}</div>
              <div>{t("routers.last_seen")}: {r.last_seen ? new Date(r.last_seen).toLocaleString() : "—"}</div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
              <button data-testid={`test-router-${r.id}`} onClick={() => test(r)} disabled={testing === r.id}
                className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-1.5 text-xs font-medium transition-colors">
                {testing === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ServerCog className="h-3.5 w-3.5" />} {t("routers.test_conn")}
              </button>
              <button data-testid={`wizard-router-${r.id}`} onClick={() => openWizard(r)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600/15 text-blue-300 hover:bg-blue-600/25 px-3 py-1.5 text-xs font-medium ring-1 ring-blue-600/30 transition-colors">
                {t("routers.wizard")}
              </button>
              <button data-testid={`live-toggle-${r.id}`} onClick={() => toggleLive(r)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium ring-1 transition-colors ${r.live_mode ? "bg-emerald-600/15 text-emerald-300 ring-emerald-600/30 hover:bg-emerald-600/25" : "bg-white/5 text-slate-400 ring-white/10 hover:bg-white/10"}`}>
                <Zap className="h-3.5 w-3.5" /> {r.live_mode ? "Live ON" : "Live OFF"}
              </button>
              <button data-testid={`delete-router-${r.id}`} onClick={() => del(r)} className="inline-flex items-center rounded-lg hover:bg-rose-500/10 px-2 py-1.5 text-rose-400 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Create modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{t("routers.new")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("nav.structures")}</label>
              <select data-testid="router-structure-select" value={form.structure_id} onChange={(e) => setForm({ ...form, structure_id: e.target.value })}
                className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm">
                {structures.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            {[["name", t("common.name")], ["model", t("routers.model")], ["host", t("routers.host")], ["api_port", t("routers.port")], ["ros_version", "RouterOS"], ["username", "Username"], ["password", "Password"]].map(([k, label]) => (
              <div key={k}>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
                <input data-testid={`router-input-${k}`} type={k === "password" ? "password" : "text"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ))}
          </div>
          <label className="flex items-start gap-2.5 mt-1 rounded-lg border border-[#1F2937] bg-[#0B0F17] p-3 cursor-pointer">
            <input data-testid="router-live-mode" type="checkbox" checked={form.live_mode} onChange={(e) => setForm({ ...form, live_mode: e.target.checked })} className="accent-emerald-600 mt-0.5" />
            <span>
              <span className="text-sm font-semibold">Modalità Live (MikroTik reale)</span>
              <p className="text-xs text-slate-500 mt-0.5">Se attiva, il pannello si collega davvero al router via API RouterOS (host, porta, credenziali sopra) per test connessione e provisioning degli utenti hotspot al check-in/out. Se disattiva, tutto resta simulato.</p>
            </span>
          </label>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm text-slate-400">{t("common.cancel")}</button>
            <button data-testid="save-router-btn" onClick={create} className="rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2 text-sm font-semibold">{t("common.create")}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Wizard modal */}
      <Dialog open={!!wizard} onOpenChange={(v) => !v && setWizard(null)}>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-xl">
          <DialogHeader><DialogTitle className="font-display">{t("routers.wizard")} — {wizard?.router.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400 -mt-2">{t("routers.wizard_desc")}</p>
          {wizard && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="RADIUS Host" value={wizard.config.radius_host} />
                <Info label={t("routers.shared_secret")} value={wizard.config.shared_secret} mono />
                <Info label="Auth Port" value={wizard.config.auth_port} />
                <Info label="Acct Port" value={wizard.config.acct_port} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">RouterOS Script</label>
                  <button data-testid="copy-script-btn" onClick={() => { navigator.clipboard.writeText(wizard.config.script); toast.success("✓ Copiato"); }}
                    className="inline-flex items-center gap-1.5 text-xs text-blue-300 hover:text-blue-200"><Copy className="h-3.5 w-3.5" /> {t("routers.copy_script")}</button>
                </div>
                <pre className="rounded-lg bg-[#0B0F17] border border-[#1F2937] p-3 text-xs font-mono text-emerald-300 whitespace-pre-wrap overflow-x-auto">{wizard.config.script}</pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div className="rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-sm ${mono ? "font-mono text-cyan-400" : ""}`}>{value}</div>
    </div>
  );
}
